import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./horizonTable.css";

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function toLocalDateString(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toDateInputValue(d) {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  return toLocalDateString(date);
}

function getEntryWorkerId(entry) {
  if (!entry?.workerId) return null;
  if (typeof entry.workerId === "object") return entry.workerId._id ? String(entry.workerId._id) : null;
  return String(entry.workerId);
}

function getPrimaryWorkerId(sheet) {
  if (!sheet || !Array.isArray(sheet.workerIds) || sheet.workerIds.length === 0) return null;
  const first = sheet.workerIds[0];
  return String(typeof first === "object" ? first?._id : first);
}

function buildSiteDateRanges(dateToSite) {
  const bySite = new Map();
  const sortedDates = Array.from(dateToSite.keys()).sort();
  sortedDates.forEach((ymd) => {
    const site = dateToSite.get(ymd);
    if (!site) return;
    const sid = String(site._id);
    if (!bySite.has(sid)) {
      bySite.set(sid, { siteId: sid, siteName: site.siteName, dates: [] });
    }
    bySite.get(sid).dates.push(ymd);
  });

  const ranges = [];
  bySite.forEach((item) => {
    const sorted = item.dates.sort();
    let rangeStart = sorted[0];
    let rangeEnd = sorted[0];
    for (let i = 1; i < sorted.length; i += 1) {
      const prev = parseYYYYMMDDLocal(rangeEnd);
      const nextDay = new Date(prev);
      nextDay.setDate(nextDay.getDate() + 1);
      if (toLocalDateString(nextDay) === sorted[i]) {
        rangeEnd = sorted[i];
      } else {
        ranges.push({ siteId: item.siteId, siteName: item.siteName, dateFrom: rangeStart, dateTo: rangeEnd });
        rangeStart = sorted[i];
        rangeEnd = sorted[i];
      }
    }
    ranges.push({ siteId: item.siteId, siteName: item.siteName, dateFrom: rangeStart, dateTo: rangeEnd });
  });
  return ranges.sort((a, b) => a.dateFrom.localeCompare(b.dateFrom));
}

function getSiteForDate(ctx, ymd) {
  if (!ctx || !ymd) return null;
  if (ctx.dateToSite instanceof Map) return ctx.dateToSite.get(ymd) || null;
  if (ctx.dateToSite && typeof ctx.dateToSite === "object") return ctx.dateToSite[ymd] || null;
  return null;
}

function getSiteIdForDate(ctx, ymd) {
  const site = getSiteForDate(ctx, ymd);
  return site?._id ? String(site._id) : "";
}

function buildAllWorkersWorkSheetContext(sheets) {
  const byWorker = new Map();

  const upsert = (workerId) => {
    const wid = String(workerId);
    if (!byWorker.has(wid)) {
      byWorker.set(wid, { sites: new Map(), allowedDates: new Set(), dateToSite: new Map() });
    }
    return byWorker.get(wid);
  };

  (Array.isArray(sheets) ? sheets : []).forEach((sheet) => {
    if ((sheet?.status || "active") !== "active") return;

    const siteObj = sheet.siteId;
    const siteId = typeof siteObj === "object" ? siteObj?._id : siteObj;
    const siteEntry =
      siteId != null
        ? {
            _id: String(siteId),
            siteName: typeof siteObj === "object" && siteObj?.siteName ? siteObj.siteName : String(siteId),
          }
        : null;

    const primaryId = getPrimaryWorkerId(sheet);
    const entries = Array.isArray(sheet.entries) ? sheet.entries : [];
    const workerIdSet = new Set();

    (Array.isArray(sheet.workerIds) ? sheet.workerIds : []).forEach((w) => {
      const id = typeof w === "object" ? w?._id : w;
      if (id) workerIdSet.add(String(id));
    });
    entries.forEach((e) => {
      const eid = getEntryWorkerId(e);
      if (eid) workerIdSet.add(String(eid));
    });

    workerIdSet.forEach((wid) => {
      const ctx = upsert(wid);
      if (siteEntry) ctx.sites.set(siteEntry._id, siteEntry);
    });

    entries.forEach((entry) => {
      if (!entry?.date || entry.isOnRest === true) return;
      let wid = getEntryWorkerId(entry);
      if (!wid && primaryId) wid = primaryId;
      if (!wid) return;

      const ctx = upsert(wid);
      const ymd = toLocalDateString(new Date(entry.date));
      if (ymd && siteEntry) {
        ctx.allowedDates.add(ymd);
        ctx.dateToSite.set(ymd, siteEntry);
        ctx.sites.set(siteEntry._id, siteEntry);
      }
    });
  });

  const out = {};
  byWorker.forEach((ctx, wid) => {
    const allowedDates = Array.from(ctx.allowedDates).sort();
    let dateFrom = "";
    let dateTo = "";
    if (allowedDates.length > 0) {
      dateFrom = allowedDates[0];
      dateTo = allowedDates[allowedDates.length - 1];
    }
    const dateToSite = {};
    ctx.dateToSite.forEach((site, ymd) => {
      dateToSite[ymd] = site;
    });
    out[wid] = {
      sites: Array.from(ctx.sites.values()),
      dateFrom,
      dateTo,
      allowedDates,
      dateToSite,
      siteDateRanges: buildSiteDateRanges(ctx.dateToSite),
    };
  });
  return out;
}

function normalizeSelectedDates(dates) {
  if (!Array.isArray(dates)) return [];
  return [...new Set(dates.filter((d) => typeof d === "string" && d))].sort();
}

function buildDateFieldsFromSelection(selectedDates) {
  const sorted = normalizeSelectedDates(selectedDates);
  if (sorted.length === 0) {
    return { selectedDates: [], dateFrom: "", dateTo: "" };
  }
  return {
    selectedDates: sorted,
    dateFrom: sorted[0],
    dateTo: sorted[sorted.length - 1],
  };
}

function buildDateSitesFromSelection(ctx, selectedDates) {
  return normalizeSelectedDates(selectedDates)
    .map((date) => {
      const siteId = getSiteIdForDate(ctx, date);
      return siteId ? { date, siteId } : null;
    })
    .filter(Boolean);
}

function resolvePrimarySiteId(ctx, selectedDates) {
  const siteIds = [
    ...new Set(
      normalizeSelectedDates(selectedDates)
        .map((date) => getSiteIdForDate(ctx, date))
        .filter(Boolean)
    ),
  ];
  return siteIds[0] || "";
}

function getStandbySelectedDates(item) {
  if (Array.isArray(item?.selectedDates) && item.selectedDates.length > 0) {
    return normalizeSelectedDates(item.selectedDates);
  }
  const from = toDateInputValue(item?.dateFrom);
  const to = toDateInputValue(item?.dateTo);
  if (from && to && from === to) return [from];
  if (from && to) return [from, to];
  if (from) return [from];
  return [];
}

function formatStandbyDatesDisplay(item) {
  const dates = getStandbySelectedDates(item);
  if (dates.length === 0) return "—";
  return dates.map((d) => formatDate(d)).join(", ");
}

function formatStandbySiteDisplay(item) {
  const dateSites = Array.isArray(item?.dateSites) ? item.dateSites : [];
  if (dateSites.length > 0) {
    const siteIds = new Set(dateSites.map((entry) => String(entry.siteId?._id || entry.siteId || "")));
    if (siteIds.size > 1) return "Multiple";
  }
  return item.siteId?.siteName || "—";
}

function applyWorkerToStandbyForm(form, workerId, workersMap) {
  return {
    ...form,
    workerId: workerId || "",
    siteId: "",
    dateFrom: "",
    dateTo: "",
    selectedDates: [],
    dateSites: [],
  };
}

function formatDate(d) {
  if (!d) return "—";
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (typeof d === "string") {
    const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const year = Number(m[1]);
      const month = Number(m[2]);
      const day = Number(m[3]);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return `${String(day).padStart(2, "0")} ${monthNames[month - 1]} ${year}`;
      }
    }
  }
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  const day = date.getUTCDate();
  const month = date.getUTCMonth();
  const year = date.getUTCFullYear();
  return `${String(day).padStart(2, "0")} ${monthNames[month]} ${year}`;
}

function isSavedStandbyRecord(item) {
  if (!item) return false;
  const workerId = item.workerId && (typeof item.workerId === "object" ? item.workerId._id : item.workerId);
  const siteId = item.siteId && (typeof item.siteId === "object" ? item.siteId._id : item.siteId);
  if (!workerId || !siteId || !item.dateFrom || !item.dateTo) return false;
  const from = new Date(item.dateFrom);
  const to = new Date(item.dateTo);
  return !Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime());
}

const modalDialogStyle = {
  maxWidth: "560px",
  width: "calc(100% - 32px)",
  margin: "0 auto",
};

const modalContentStyle = {
  borderRadius: "12px",
  border: "none",
  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  maxHeight: "min(90vh, 640px)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const modalBodyStyle = {
  overflowY: "auto",
  flex: "1 1 auto",
  padding: "20px 24px",
  background: "#fff",
};

const modalHeaderStyle = {
  borderBottom: "1px solid #e2e8f0",
  background: "#f8fafc",
  padding: "16px 20px",
  borderRadius: "12px 12px 0 0",
  flexShrink: 0,
};

const modalFooterStyle = {
  borderTop: "1px solid #e2e8f0",
  background: "#fff",
  padding: "14px 20px",
  flexShrink: 0,
};

function getWorkerName(worker) {
  if (!worker) return "—";
  if (typeof worker === "object") {
    const name = `${worker.firstName || ""} ${worker.lastName || ""}`.trim();
    return name || worker.email || "—";
  }
  return "—";
}

function parseYYYYMMDDLocal(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const parts = ymd.split("-");
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const mo = Number(parts[1]);
  const day = Number(parts[2]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(day)) return null;
  return new Date(y, mo - 1, day);
}

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function getWeekStartMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setHours(0, 0, 0, 0);
  d.setDate(diff);
  return d;
}

function getWeekDays(weekStart) {
  const days = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    days.push(d);
  }
  return days;
}

function buildWeekStartsFromAllowed(allowedDates) {
  if (!Array.isArray(allowedDates) || allowedDates.length === 0) return [];
  const weekStarts = new Set();
  allowedDates.forEach((ymd) => {
    const d = parseYYYYMMDDLocal(ymd);
    if (d) weekStarts.add(getWeekStartMonday(d).getTime());
  });
  return Array.from(weekStarts)
    .sort((a, b) => a - b)
    .map((t) => new Date(t));
}

function StandbyWeeklyDatePicker({ selectedDates, onChange, allowedDates }) {
  const allowedList = Array.isArray(allowedDates) ? allowedDates : [];
  const allowedKey = allowedList.join("|");
  const allowedSet = useMemo(() => new Set(allowedList), [allowedKey]);
  const weekStarts = useMemo(() => buildWeekStartsFromAllowed(allowedList), [allowedKey]);
  const [weekIndex, setWeekIndex] = useState(0);
  const [selectedSet, setSelectedSet] = useState(() => new Set());
  const allowedKeyRef = useRef(allowedKey);
  const selectionKey = normalizeSelectedDates(selectedDates).join("|");

  useEffect(() => {
    if (allowedKeyRef.current === allowedKey) return;
    allowedKeyRef.current = allowedKey;
    setWeekIndex(0);
    setSelectedSet(new Set());
  }, [allowedKey]);

  useEffect(() => {
    setSelectedSet(new Set(normalizeSelectedDates(selectedDates)));
  }, [selectionKey]);

  const currentWeekStart = weekStarts[weekIndex] || null;
  const weekDays = useMemo(
    () => (currentWeekStart ? getWeekDays(currentWeekStart) : []),
    [currentWeekStart ? currentWeekStart.getTime() : 0]
  );
  const weekEnd = weekDays.length === 7 ? weekDays[6] : null;

  const toggleDay = useCallback(
    (ymd, enabled) => {
      if (!enabled) return;
      setSelectedSet((prev) => {
        const next = new Set(prev);
        if (next.has(ymd)) next.delete(ymd);
        else next.add(ymd);
        const sorted = Array.from(next).sort();
        onChange(buildDateFieldsFromSelection(sorted));
        return next;
      });
    },
    [onChange]
  );

  const goPrevWeek = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setWeekIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNextWeek = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setWeekIndex((i) => Math.min(weekStarts.length - 1, i + 1));
    },
    [weekStarts.length]
  );

  const selectedSorted = useMemo(() => Array.from(selectedSet).sort(), [selectedSet]);

  const navBtnStyle = (disabled) => ({
    width: "36px",
    height: "36px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    background: disabled ? "#f8fafc" : "#fff",
    color: disabled ? "#cbd5e1" : "#8b1634",
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: "13px",
    boxShadow: disabled ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
    transition: "background-color 0.15s ease, border-color 0.15s ease",
  });

  if (allowedList.length === 0) {
    return (
      <div className="mb-3">
        <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "8px", display: "block" }}>
          Select dates
        </label>
        <div
          style={{
            padding: "20px 16px",
            borderRadius: "12px",
            border: "1px dashed #e2e8f0",
            color: "#94a3b8",
            fontSize: "13px",
            textAlign: "center",
            background: "#fafbfc",
          }}
        >
          Select a worker to view available shift dates
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <label className="form-label" style={{ fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "8px", display: "block" }}>
        Select dates
      </label>
      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: "14px",
          padding: "16px 12px",
          background: "linear-gradient(180deg, #fafbfc 0%, #ffffff 100%)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        {currentWeekStart && weekEnd ? (
          <div
            style={{
              textAlign: "center",
              fontSize: "13px",
              color: "#475569",
              marginBottom: "14px",
              fontWeight: 600,
              letterSpacing: "0.01em",
            }}
          >
            {formatDate(toLocalDateString(currentWeekStart))} – {formatDate(toLocalDateString(weekEnd))}
          </div>
        ) : null}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <button type="button" aria-label="Previous week" disabled={weekIndex <= 0} onClick={goPrevWeek} style={navBtnStyle(weekIndex <= 0)}>
            <i className="fa fa-chevron-left" />
          </button>
          <div
            style={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
              gap: "8px",
              minWidth: 0,
            }}
          >
            {weekDays.map((d, idx) => {
              const ymd = toLocalDateString(d);
              const enabled = allowedSet.has(ymd);
              const selected = selectedSet.has(ymd);
              const monthShort = d.toLocaleDateString("en-US", { month: "short" });
              return (
                <button
                  key={ymd}
                  type="button"
                  disabled={!enabled}
                  aria-pressed={selected}
                  aria-label={`${WEEKDAY_LABELS[idx]} ${d.getDate()} ${monthShort}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleDay(ymd, enabled);
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "2px",
                    padding: "10px 4px",
                    minHeight: "72px",
                    borderRadius: "12px",
                    border: selected ? "2px solid #8b1634" : "1px solid #e2e8f0",
                    background: !enabled ? "#f1f5f9" : selected ? "rgba(139, 22, 52, 0.1)" : "#fff",
                    color: !enabled ? "#94a3b8" : selected ? "#8b1634" : "#1e293b",
                    opacity: enabled ? 1 : 0.5,
                    cursor: enabled ? "pointer" : "not-allowed",
                    userSelect: "none",
                    minWidth: 0,
                    boxShadow: selected ? "0 2px 8px rgba(139, 22, 52, 0.15)" : "0 1px 2px rgba(0,0,0,0.03)",
                    transition: "border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease",
                    transform: selected ? "translateY(-1px)" : "none",
                  }}
                  onMouseEnter={(e) => {
                    if (!enabled || selected) return;
                    e.currentTarget.style.borderColor = "rgba(139, 22, 52, 0.35)";
                    e.currentTarget.style.background = "rgba(139, 22, 52, 0.04)";
                  }}
                  onMouseLeave={(e) => {
                    if (!enabled || selected) return;
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.background = "#fff";
                  }}
                >
                  <span style={{ fontSize: "10px", fontWeight: 600, color: selected ? "#8b1634" : "#64748b", textTransform: "uppercase" }}>
                    {WEEKDAY_LABELS[idx]}
                  </span>
                  <span style={{ fontSize: "18px", fontWeight: 700, lineHeight: 1.1 }}>{d.getDate()}</span>
                  <span style={{ fontSize: "10px", color: selected ? "#8b1634" : "#94a3b8" }}>{monthShort}</span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            aria-label="Next week"
            disabled={weekIndex >= weekStarts.length - 1}
            onClick={goNextWeek}
            style={navBtnStyle(weekIndex >= weekStarts.length - 1)}
          >
            <i className="fa fa-chevron-right" />
          </button>
        </div>
        <div
          style={{
            marginTop: "14px",
            padding: "10px 12px",
            borderRadius: "10px",
            background: selectedSorted.length > 0 ? "rgba(139, 22, 52, 0.06)" : "#f8fafc",
            border: selectedSorted.length > 0 ? "1px solid rgba(139, 22, 52, 0.15)" : "1px solid #f1f5f9",
            fontSize: "12px",
            color: selectedSorted.length > 0 ? "#8b1634" : "#64748b",
            textAlign: "center",
            lineHeight: 1.5,
            fontWeight: selectedSorted.length > 0 ? 600 : 500,
          }}
        >
          {selectedSorted.length > 0
            ? `Selected (${selectedSorted.length}): ${selectedSorted.map((d) => formatDate(d)).join(", ")}`
            : "Tap days to select your on-call standby dates"}
        </div>
      </div>
    </div>
  );
}

const emptyForm = {
  workerId: "",
  siteId: "",
  dateFrom: "",
  dateTo: "",
  selectedDates: [],
  dateSites: [],
  status: "active",
  notes: "",
};

function FatigueWorkSheetsStandbyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [standbys, setStandbys] = useState([]);
  const [sites, setSites] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [newStandby, setNewStandby] = useState({ ...emptyForm });
  const [statusFilter, setStatusFilter] = useState("");
  const [workersWorkSheetMap, setWorkersWorkSheetMap] = useState({});

  const urlParams = new URLSearchParams(location.search || "");
  const workerIdFromUrl = urlParams.get("workerId") || "";
  const startDateFromUrl = urlParams.get("startDate") || "";
  const endDateFromUrl = urlParams.get("endDate") || "";
  const fromWorkSheetsContext = Object.keys(workersWorkSheetMap).length > 0;

  useEffect(() => {
    fetchStandbys();
    fetchSites();
    fetchAppUsers();
    fetchWorkSheetContext();
  }, [location.search]);

  async function fetchStandbys() {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/on-call-standby", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success && Array.isArray(data.data)) {
        setStandbys(data.data);
      } else {
        setStandbys([]);
        if (!res.ok) toast.error(data.message || "Failed to load standby list");
      }
    } catch {
      setStandbys([]);
      toast.error("Failed to load standby list");
    } finally {
      setLoading(false);
    }
  }

  async function fetchSites() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sites", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) setSites(data.data);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) setAppUsers(data.data);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function fetchWorkSheetContext() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/fatigue-work-sheets", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setWorkersWorkSheetMap({});
        return;
      }
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setWorkersWorkSheetMap(buildAllWorkersWorkSheetContext(data.data));
      } else {
        setWorkersWorkSheetMap({});
      }
    } catch (e) {
      console.error(e);
      setWorkersWorkSheetMap({});
    }
  }

  const workerOptionsForForm = fromWorkSheetsContext
    ? appUsers.filter((u) => workersWorkSheetMap[String(u._id)])
    : appUsers;

  const getSelectedWorkerContext = (workerId) =>
    workersWorkSheetMap[String(workerId)] || {
      sites: [],
      dateFrom: "",
      dateTo: "",
      allowedDates: [],
      dateToSite: {},
      siteDateRanges: [],
    };

  const getDefaultAddForm = () => {
    const initialWorkerId =
      workerIdFromUrl ||
      (workerOptionsForForm.length === 1 ? String(workerOptionsForForm[0]._id) : "");
    const base = {
      ...emptyForm,
      status: "active",
      notes: "",
    };
    if (!initialWorkerId) return base;
    const applied = applyWorkerToStandbyForm(base, initialWorkerId, workersWorkSheetMap);
    if (startDateFromUrl && endDateFromUrl && startDateFromUrl === endDateFromUrl && initialWorkerId === workerIdFromUrl) {
      const ctx = workersWorkSheetMap[String(initialWorkerId)];
      const dateFields = buildDateFieldsFromSelection([startDateFromUrl]);
      applied.selectedDates = dateFields.selectedDates;
      applied.dateFrom = dateFields.dateFrom;
      applied.dateTo = dateFields.dateTo;
      applied.dateSites = buildDateSitesFromSelection(ctx, dateFields.selectedDates);
      applied.siteId = resolvePrimarySiteId(ctx, dateFields.selectedDates);
    }
    return applied;
  };

  const goBack = () => {
    const next = new URLSearchParams(location.search || "");
    const search = next.toString() ? `?${next.toString()}` : "";
    navigate({ pathname: "/admin/fatigue/work-sheets", search });
  };

  const openAdd = () => {
    setNewStandby(getDefaultAddForm());
    setShowAddModal(true);
  };

  const openEdit = (item) => {
    const workerId = typeof item.workerId === "object" ? item.workerId?._id : item.workerId;
    const ctx = workersWorkSheetMap[String(workerId)];
    const selectedDates = getStandbySelectedDates(item);
    const dateFields = buildDateFieldsFromSelection(selectedDates);
    const dateSites =
      Array.isArray(item.dateSites) && item.dateSites.length > 0
        ? item.dateSites.map((entry) => ({
            date: entry.date,
            siteId: typeof entry.siteId === "object" ? entry.siteId?._id : entry.siteId,
          }))
        : buildDateSitesFromSelection(ctx, selectedDates);
    const siteId =
      resolvePrimarySiteId(ctx, selectedDates) ||
      (typeof item.siteId === "object" ? item.siteId?._id : item.siteId) ||
      "";
    setEditItem({
      _id: item._id,
      workerId,
      siteId,
      selectedDates: dateFields.selectedDates,
      dateFrom: dateFields.dateFrom,
      dateTo: dateFields.dateTo,
      dateSites,
      status: item.status === "inactive" ? "inactive" : "active",
      notes: item.notes || "",
    });
    setShowEditModal(true);
  };

  const handleCreate = async () => {
    const selectedDates = normalizeSelectedDates(newStandby.selectedDates);
    if (!newStandby.workerId || !newStandby.siteId || selectedDates.length === 0) {
      toast.error("Worker, Site, and at least one date are required");
      return;
    }
    if (fromWorkSheetsContext && newStandby.workerId) {
      const ctx = getSelectedWorkerContext(newStandby.workerId);
      const allowed = new Set(ctx.allowedDates || []);
      const invalid = selectedDates.filter((d) => !allowed.has(d));
      if (invalid.length > 0) {
        toast.error("One or more selected dates are not available for this worker");
        return;
      }
      if (selectedDates.some((d) => !getSiteIdForDate(ctx, d))) {
        toast.error("No site found for one or more selected dates");
        return;
      }
    }
    setModalLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/on-call-standby", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...newStandby,
          selectedDates,
          dateFrom: selectedDates[0],
          dateTo: selectedDates[selectedDates.length - 1],
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast.error(data.message || "Failed to create standby");
        return;
      }
      toast.success("On-call standby added");
      setShowAddModal(false);
      fetchStandbys();
    } catch {
      toast.error("Failed to create standby");
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editItem?._id) return;
    const selectedDates = normalizeSelectedDates(editItem.selectedDates);
    if (!editItem.workerId || !editItem.siteId || selectedDates.length === 0) {
      toast.error("Worker, Site, and at least one date are required");
      return;
    }
    if (fromWorkSheetsContext && editItem.workerId) {
      const ctx = getSelectedWorkerContext(editItem.workerId);
      const allowed = new Set(ctx.allowedDates || []);
      const invalid = selectedDates.filter((d) => !allowed.has(d));
      if (invalid.length > 0) {
        toast.error("One or more selected dates are not available for this worker");
        return;
      }
      if (selectedDates.some((d) => !getSiteIdForDate(ctx, d))) {
        toast.error("No site found for one or more selected dates");
        return;
      }
    }
    setModalLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/on-call-standby/" + editItem._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...editItem,
          selectedDates,
          dateFrom: selectedDates[0],
          dateTo: selectedDates[selectedDates.length - 1],
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast.error(data.message || "Failed to update standby");
        return;
      }
      toast.success("On-call standby updated");
      setShowEditModal(false);
      setEditItem(null);
      fetchStandbys();
    } catch {
      toast.error("Failed to update standby");
    } finally {
      setModalLoading(false);
    }
  };

  const filtered = standbys
    .filter(isSavedStandbyRecord)
    .filter((s) => {
      if (!workerIdFromUrl) return true;
      const wid = s.workerId && (typeof s.workerId === "object" ? s.workerId._id : s.workerId);
      return String(wid || "") === String(workerIdFromUrl);
    })
    .filter((s) => !statusFilter || (s.status || "active") === statusFilter);
  const canAddStandby = !(workerIdFromUrl && filtered.length > 0);
  const cellEllipsis = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

  const renderFormFields = (form, setForm, { isAdd = false } = {}) => {
    const selectedCtx = form.workerId
      ? getSelectedWorkerContext(form.workerId)
      : { sites: [], dateFrom: "", dateTo: "", allowedDates: [], siteDateRanges: [] };
    const siteOptionsForForm = sites;
    const lockWorker = isAdd;
    const allowedDates = fromWorkSheetsContext && form.workerId ? selectedCtx.allowedDates : [];
    const selectedDates = normalizeSelectedDates(form.selectedDates);
    const handleDateChange = (dateFields) => {
      const sorted = normalizeSelectedDates(dateFields.selectedDates);
      const next = {
        ...form,
        ...buildDateFieldsFromSelection(sorted),
      };
      if (fromWorkSheetsContext && sorted.length > 0) {
        next.dateSites = buildDateSitesFromSelection(selectedCtx, sorted);
        next.siteId = resolvePrimarySiteId(selectedCtx, sorted);
      }
      setForm(next);
    };
    const readOnlyFieldStyle = {
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      background: "#f8fafc",
      color: "#64748b",
    };

    return (
    <>
      <div className="mb-3">
        <label className="form-label" style={{ fontWeight: 600, color: "#8b1634" }}>Worker</label>
        <select
          className="form-select"
          value={form.workerId}
          onChange={(e) => {
            const nextWorkerId = e.target.value;
            if (fromWorkSheetsContext && nextWorkerId) {
              setForm(applyWorkerToStandbyForm(form, nextWorkerId, workersWorkSheetMap));
            } else {
              setForm({ ...form, workerId: nextWorkerId, siteId: "", dateFrom: "", dateTo: "", selectedDates: [], dateSites: [] });
            }
          }}
          disabled={lockWorker}
          style={lockWorker ? readOnlyFieldStyle : { borderRadius: "8px", border: "1px solid #e2e8f0" }}
        >
          <option value="">Select worker</option>
          {workerOptionsForForm.map((u) => (
            <option key={u._id} value={u._id}>
              {`${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email}
            </option>
          ))}
        </select>
      </div>
      {fromWorkSheetsContext && form.workerId && selectedCtx.siteDateRanges?.length > 0 ? (
        <div
          className="mb-3"
          style={{
            padding: "10px 12px",
            borderRadius: "10px",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
          }}
        >
          <div style={{ fontWeight: 600, fontSize: "12px", color: "#8b1634", marginBottom: "6px" }}>
            Sites & available dates
          </div>
          {selectedCtx.siteDateRanges.map((range) => (
            <div
              key={`${range.siteId}-${range.dateFrom}`}
              style={{ fontSize: "12px", color: "#475569", lineHeight: 1.5, marginBottom: "4px" }}
            >
              <span style={{ fontWeight: 600, color: "#334155" }}>{range.siteName}</span>
              {" — "}
              {range.dateFrom === range.dateTo
                ? formatDate(range.dateFrom)
                : `${formatDate(range.dateFrom)} to ${formatDate(range.dateTo)}`}
            </div>
          ))}
        </div>
      ) : !fromWorkSheetsContext ? (
        <div className="mb-3">
          <label className="form-label" style={{ fontWeight: 600, color: "#8b1634" }}>Site</label>
          <select
            className="form-select"
            value={form.siteId}
            onChange={(e) => setForm({ ...form, siteId: e.target.value })}
            style={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
          >
            <option value="">Select site</option>
            {siteOptionsForForm.map((s) => (
              <option key={s._id} value={s._id}>
                {s.siteName || s._id}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <StandbyWeeklyDatePicker
        selectedDates={selectedDates}
        allowedDates={allowedDates}
        onChange={handleDateChange}
      />
      {fromWorkSheetsContext && selectedDates.length > 0 ? (
        <div
          className="mb-3"
          style={{
            marginTop: "-8px",
            padding: "8px 10px",
            borderRadius: "8px",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            fontSize: "12px",
            color: "#475569",
          }}
        >
          {selectedDates.map((date) => {
            const site = getSiteForDate(selectedCtx, date);
            return (
              <div key={date} style={{ lineHeight: 1.5 }}>
                <strong style={{ color: "#334155" }}>{formatDate(date)}</strong>
                {site?.siteName ? ` — ${site.siteName}` : ""}
              </div>
            );
          })}
        </div>
      ) : null}
      <div className="mb-3">
        <label className="form-label" style={{ fontWeight: 600, color: "#8b1634" }}>Notes</label>
        <textarea
          className="form-control"
          rows={3}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Optional notes"
          style={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
        />
      </div>
    </>
    );
  };

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "20px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
        padding: "24px",
        marginBottom: "24px",
      }}
    >
      <div style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            type="button"
            onClick={goBack}
            style={{
              height: "34px",
              padding: "0 10px",
              borderRadius: "999px",
              border: "1px solid rgba(139, 22, 52, 0.25)",
              background: "rgba(139, 22, 52, 0.06)",
              fontSize: "13px",
              fontWeight: 600,
              color: "#8b1634",
              cursor: "pointer",
            }}
          >
            <i className="fa fa-arrow-left" style={{ marginRight: "6px" }} />
            Back to Work Sheets
          </button>
          <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#1a202c", margin: 0 }}>On-Call Standby</h2>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px" }}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {canAddStandby ? (
            <button
              type="button"
              onClick={openAdd}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "none",
                background: "#8b1634",
                color: "#fff",
                fontWeight: 600,
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              <i className="fa fa-plus" style={{ marginRight: "6px" }} />
              Add Standby
            </button>
          ) : null}
        </div>
      </div>

      <p style={{ color: "#718096", fontSize: "14px", marginBottom: "16px" }}>
        Mark engineers as on-call standby. Only workers on active standby can log on-call callouts in the mobile app.
      </p>

      <div style={{ overflowX: "auto", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(180px,1fr) minmax(180px,1fr) minmax(120px,0.8fr) minmax(100px,0.7fr) minmax(140px,1fr) minmax(100px,0.7fr)",
            minWidth: "800px",
            background: "#e8f4fc",
            padding: "12px 16px",
            borderBottom: "1px solid #e2e8f0",
            gap: "12px",
          }}
        >
          <div style={{ fontWeight: 600, color: "#8b1634", fontSize: "13px" }}>Worker</div>
          <div style={{ fontWeight: 600, color: "#8b1634", fontSize: "13px" }}>Site</div>
          <div style={{ fontWeight: 600, color: "#8b1634", fontSize: "13px" }}>Date</div>
          <div style={{ fontWeight: 600, color: "#8b1634", fontSize: "13px" }}>Status</div>
          <div style={{ fontWeight: 600, color: "#8b1634", fontSize: "13px" }}>Notes</div>
          <div style={{ fontWeight: 600, color: "#8b1634", fontSize: "13px" }}>Actions</div>
        </div>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#8b1634" }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#718096" }}>No standby records found.</div>
        ) : (
          filtered.map((item) => {
            const isActive = (item.status || "active") === "active";
            return (
              <div
                key={item._id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(180px,1fr) minmax(180px,1fr) minmax(120px,0.8fr) minmax(100px,0.7fr) minmax(140px,1fr) minmax(100px,0.7fr)",
                  minWidth: "800px",
                  padding: "14px 16px",
                  borderBottom: "1px solid #f1f5f9",
                  gap: "12px",
                  alignItems: "center",
                }}
              >
                <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>{getWorkerName(item.workerId)}</div>
                <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>
                  {formatStandbySiteDisplay(item)}
                </div>
                <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }} title={formatStandbyDatesDisplay(item)}>
                  {formatStandbyDatesDisplay(item)}
                </div>
                <div>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      padding: "4px 10px",
                      borderRadius: "999px",
                      background: isActive ? "rgba(16, 185, 129, 0.14)" : "rgba(107, 114, 128, 0.14)",
                      color: isActive ? "#047857" : "#374151",
                    }}
                  >
                    {isActive ? "Active" : "Inactive"}
                  </span>
                </div>
                <div style={{ color: "#64748b", fontSize: "13px", ...cellEllipsis }} title={item.notes || ""}>
                  {item.notes || "—"}
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    type="button"
                    onClick={() => openEdit(item)}
                    style={{ background: "transparent", border: "none", color: "#22c55e", cursor: "pointer", fontSize: "16px" }}
                    title="Edit"
                  >
                    <i className="fa fa-edit" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {showAddModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.4)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered" style={modalDialogStyle}>
            <div className="modal-content" style={modalContentStyle}>
              <div className="modal-header" style={modalHeaderStyle}>
                <h5 className="modal-title" style={{ color: "#8b1634", fontWeight: 600, fontSize: "18px", margin: 0 }}>Add On-Call Standby</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddModal(false)} style={{ opacity: 0.7 }} />
              </div>
              <div className="modal-body" style={modalBodyStyle}>{renderFormFields(newStandby, setNewStandby, { isAdd: true })}</div>
              <div className="modal-footer" style={modalFooterStyle}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)} disabled={modalLoading}>
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: "#8b1634", color: "#fff", minWidth: "88px" }} onClick={handleCreate} disabled={modalLoading}>
                  {modalLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editItem && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.4)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered" style={modalDialogStyle}>
            <div className="modal-content" style={modalContentStyle}>
              <div className="modal-header" style={modalHeaderStyle}>
                <h5 className="modal-title" style={{ color: "#8b1634", fontWeight: 600, fontSize: "18px", margin: 0 }}>Edit On-Call Standby</h5>
                <button type="button" className="btn-close" onClick={() => { setShowEditModal(false); setEditItem(null); }} style={{ opacity: 0.7 }} />
              </div>
              <div className="modal-body" style={modalBodyStyle}>{renderFormFields(editItem, setEditItem)}</div>
              <div className="modal-footer" style={modalFooterStyle}>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowEditModal(false); setEditItem(null); }} disabled={modalLoading}>
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: "#8b1634", color: "#fff", minWidth: "88px" }} onClick={handleUpdate} disabled={modalLoading}>
                  {modalLoading ? "Saving..." : "Update"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FatigueWorkSheetsStandbyPage;
